import React, { useEffect, useState } from 'react';
import { fetchTodayCheckin, fetchHormoneData } from '../../lib/userDataService';
import { supabase } from '../../lib/supabase';

/**
 * BodySnapshotScreen — a premium editorial dashboard of the user's
 * body signals today, backed entirely by live Supabase data.
 *
 * Data sources (all existing tables — zero migrations required):
 *  - daily_checkins  (mood, energy_level, pain_level, symptoms JSONB, notes)
 *  - user_cycle_data (phase, day, cycle_length)  -- via useCycleData
 *  - symptom_logs    (last 7 days, for recovery-trend calc)
 *  - hormone_reports (presence flag — links to Hormone Trends)
 *
 * Missing signals render as calm "not tracked yet" prompts (never
 * broken skeletons), keeping the Apple-Health-quiet aesthetic.
 */

const N_A = null;

const ENERGY_LABEL   = ['—', 'Very low', 'Low', 'Low', 'Moderate', 'Moderate', 'Steady', 'Steady', 'High', 'High', 'Peak'];
const MOOD_LABEL     = ['—', 'Heavy', 'Heavy', 'Muted', 'Muted', 'Neutral', 'Neutral', 'Bright', 'Bright', 'Elevated', 'Elevated'];
const PAIN_LABEL     = ['None', 'Whisper', 'Whisper', 'Mild', 'Mild', 'Moderate', 'Moderate', 'Sharp', 'Sharp', 'Severe', 'Severe'];

const clamp10 = (v) => (typeof v === 'number' ? Math.max(0, Math.min(10, v)) : null);

/** Aggregate a light 7-day recovery signal from recent symptom_logs. */
async function fetch7dayRecovery(userId) {
  if (!userId) return null;
  const from = new Date(Date.now() - 7 * 86400_000).toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('symptom_logs')
    .select('log_date, fatigue, sleep_quality, mood')
    .eq('user_id', userId)
    .gte('log_date', from)
    .order('log_date', { ascending: true });
  if (error || !data?.length) return null;
  // Recovery proxy: inverse of fatigue + sleep quality (higher = better recovery)
  const scores = data.map((r) => {
    const fatigue = clamp10(r.fatigue) ?? 5;
    const sleep   = clamp10(r.sleep_quality) ?? 5;
    return Math.round(((10 - fatigue) + sleep) / 2 * 10); // 0-100
  });
  const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  const trend = scores.length >= 2
    ? scores[scores.length - 1] - scores[0]
    : 0;
  return { current: scores[scores.length - 1], average: avg, trend, days: scores.length };
}

export default function BodySnapshotScreen({ userId, phase = 'luteal', dayNumber = 1, waiting = false, cycleLength = 28 }) {
  const [state, setState] = useState({
    loading: true,
    checkin: null,      // mood / energy / pain / symptoms JSONB
    recovery: null,     // { current, average, trend, days }
    hasHormones: false,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!userId) { if (alive) setState((s) => ({ ...s, loading: false })); return; }
      const [checkin, recovery, hormones] = await Promise.all([
        fetchTodayCheckin(userId),
        fetch7dayRecovery(userId),
        fetchHormoneData(userId),
      ]);
      if (!alive) return;
      setState({ loading: false, checkin, recovery, hasHormones: !!hormones });
    })();
    return () => { alive = false; };
  }, [userId]);

  const { checkin, recovery, hasHormones, loading } = state;

  const energy = clamp10(checkin?.energy_level);
  const mood   = clamp10(checkin?.mood);
  const pain   = clamp10(checkin?.pain_level);
  const symptoms = checkin?.symptoms || null;
  const symptomCount = symptoms ? Object.values(symptoms).filter((v) => Number(v) > 0).length : 0;

  return (
    <div className="cx-sub-body" data-testid="body-snapshot-screen">
      <section className="cx-intel-hero">
        <div className="cx-intel-eyebrow">{phase.toUpperCase()} · DAY {dayNumber}{waiting ? ' · LATE' : ''}</div>
        <h1 className="cx-intel-h1">
          {loading ? 'Reading your body…' : buildHeadline({ energy, mood, pain, phase, waiting })}
        </h1>
        <div className="cx-intel-conf-chip" style={{ marginTop: 16 }}>
          <span className="cx-conf-dot" />
          {loading ? 'Loading' : `${signalsCount({ energy, mood, pain, recovery, symptomCount })} of 5 signals tracked today`}
        </div>
      </section>

      {/* Vitals row — three headline signals */}
      <section className="cx-intel-section">
        <div className="cx-intel-label">Today&rsquo;s vitals</div>
        <div className="cx-body-vitals">
          <VitalCell testid="vital-energy"   label="Energy"   value={energy} scale={10} formatter={(v) => ENERGY_LABEL[v]} />
          <VitalCell testid="vital-mood"     label="Mood"     value={mood}   scale={10} formatter={(v) => MOOD_LABEL[v]} />
          <VitalCell testid="vital-recovery" label="Recovery" value={recovery?.current} scale={100} suffix="%" hint={recovery?.trend != null ? trendArrow(recovery.trend) : null} />
        </div>
      </section>

      {/* Secondary signals */}
      <section className="cx-intel-section">
        <div className="cx-intel-label">Other signals</div>
        <div className="cx-body-list">
          <ListRow testid="row-pain"      label="Pain / cramps" value={pain != null ? PAIN_LABEL[pain] : N_A} placeholder="Log to see" />
          <ListRow testid="row-sleep"     label="Sleep quality" value={secondarySleep(checkin)} placeholder="Log to see" />
          <ListRow testid="row-hydration" label="Hydration"     value={checkin?.hydration_glasses ? `${checkin.hydration_glasses} glasses` : null} placeholder="Not tracked" />
          <ListRow testid="row-stress"    label="Stress"        value={checkin?.stress_level != null ? PAIN_LABEL[clamp10(checkin.stress_level)] : null} placeholder="Not tracked" />
          <ListRow testid="row-cravings"  label="Cravings"      value={symptoms?.cravings ? severityText(symptoms.cravings) : null} placeholder="No cravings logged" />
          <ListRow testid="row-symptoms"  label="Symptoms today" value={symptomCount ? `${symptomCount} logged` : null} placeholder="None logged" />
        </div>
      </section>

      {/* Recovery trend */}
      <section className="cx-intel-section">
        <div className="cx-intel-label">7-day recovery trend</div>
        {recovery ? (
          <div className="cx-glass" style={{ padding: 18, borderRadius: 20 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 28, fontFamily: 'Fraunces, serif', fontWeight: 400 }}>{recovery.average}%</span>
              <span style={{ fontSize: 13, color: 'var(--cx-mute)' }}>average over {recovery.days} day{recovery.days === 1 ? '' : 's'}</span>
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--cx-body)', lineHeight: 1.55 }}>
              {recoveryNarrative(recovery)}
            </div>
          </div>
        ) : (
          <EmptyBlock text="Log a few days of sleep and fatigue to unlock your recovery trend." />
        )}
      </section>

      {/* Phase changes this cycle */}
      <section className="cx-intel-section">
        <div className="cx-intel-label">Your body during {phase}</div>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--cx-body)', lineHeight: 1.6 }}>
          {PHASE_BODY_COPY[phase] || PHASE_BODY_COPY.luteal}
        </p>
      </section>

      {/* Hormones link */}
      {!hasHormones && !loading && (
        <section className="cx-intel-section">
          <div className="cx-glass" style={{ padding: 16, borderRadius: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ flex: 1, fontSize: 13.5, color: 'var(--cx-body)', lineHeight: 1.5 }}>
              Upload a hormone panel from the <strong>Health</strong> tab to overlay lab data
              on your Body Snapshot.
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ─────────── helpers & atoms ─────────── */

function VitalCell({ label, value, scale = 10, formatter, suffix, hint, testid }) {
  const hasValue = value != null;
  const pct = hasValue ? Math.round((value / scale) * 100) : 0;
  const display = hasValue
    ? (formatter ? formatter(Math.min(Math.max(Math.round(value), 0), scale)) : `${value}${suffix || ''}`)
    : '—';
  return (
    <div className="cx-vital-cell" data-testid={testid} data-k={(label || '').toLowerCase()}>
      <div className="cx-vital-value">{display}</div>
      <div className="cx-vital-bar"><span style={{ width: `${pct}%` }} /></div>
      <div className="cx-vital-label">
        {label}{hint && <span className="cx-vital-hint"> {hint}</span>}
      </div>
    </div>
  );
}

function ListRow({ label, value, placeholder, testid }) {
  return (
    <div className="cx-body-row" data-testid={testid}>
      <span className="cx-body-row-label">{label}</span>
      <span className={`cx-body-row-value ${value ? '' : 'cx-body-row-value--empty'}`}>
        {value || placeholder}
      </span>
    </div>
  );
}

function EmptyBlock({ text }) {
  return (
    <div className="cx-glass cx-empty-block" style={{ padding: 18, borderRadius: 20 }}>
      <p style={{ margin: 0, fontSize: 13.5, color: 'var(--cx-mute)', lineHeight: 1.55 }}>{text}</p>
    </div>
  );
}

const trendArrow = (trend) => trend > 0 ? '↑' : trend < 0 ? '↓' : '→';

const severityText = (v) => {
  const n = Number(v) || 0;
  return ['None', 'Mild', 'Moderate', 'Severe'][Math.min(n, 3)];
};

const secondarySleep = (checkin) => {
  const s = checkin?.symptoms?.sleep_quality;
  if (s == null) return null;
  const n = Number(s);
  return ['Excellent', 'Good', 'Poor', 'Very poor'][Math.min(n, 3)];
};

const signalsCount = ({ energy, mood, pain, recovery, symptomCount }) => {
  let n = 0;
  if (energy != null) n++;
  if (mood != null) n++;
  if (pain != null) n++;
  if (recovery) n++;
  if (symptomCount > 0) n++;
  return n;
};

const buildHeadline = ({ energy, mood, pain, phase, waiting }) => {
  if (waiting) return 'Your body is asking for patience.';
  if (energy == null && mood == null) {
    return phase === 'menstrual'
      ? 'Rest is the work today.'
      : 'Nothing logged yet — here\u2019s what to expect.';
  }
  const e = energy ?? 5;
  const m = mood ?? 5;
  if (e >= 7 && m >= 7) return 'Steady, bright, resourced.';
  if (e <= 3 || pain >= 6) return 'Your body needs gentleness today.';
  if (m <= 3) return 'Muted mood — softness wins.';
  return 'A quiet, workable day.';
};

const recoveryNarrative = (r) => {
  if (r.trend >= 8)  return 'Recovery is climbing — your rest and sleep are paying off.';
  if (r.trend <= -8) return 'Recovery is trending down. Consider protecting sleep and lowering intensity.';
  if (r.average >= 70) return 'Steady recovery. You are within your usual range.';
  if (r.average <= 40) return 'Recovery is running low. Small acts of rest add up.';
  return 'Recovery is holding steady around your baseline.';
};

const PHASE_BODY_COPY = {
  menstrual:  'Bleeding, cramps and low energy are expected. Iron and hydration matter. Rest is not indulgent — it\u2019s biology.',
  follicular: 'Estrogen is climbing. Expect brighter mood, faster recovery and more capacity for new inputs and workouts.',
  ovulatory:  'Highest fertility window. Verbal fluency, social energy and pain tolerance often peak. Watch for mid-cycle pain (mittelschmerz).',
  luteal:     'Progesterone dominates. You may feel warmer, sleepier and more tender-breasted. PMS symptoms can appear in the second half.',
  waiting:    'Delayed ovulation is common with PCOS. Track energy, sleep and mood — patterns here help the AI refine your predictions.',
};
